import type { StatefulDiaryT } from "@/types/database/diaries";
import { UUIDs } from "@/types/uuids";
import React from "react";
import type { StatefulTrackerViewT } from "../../../../../../types/database/trackers";

interface ArthrosamidAnalyticsStatProps {
	diaries: StatefulDiaryT[];
}

export function WomacAnalyticsStat({ diaries }: ArthrosamidAnalyticsStatProps) {
	// Calculate improvement statistics
	const improvementStats = React.useMemo(() => {
		const womacTrackers: StatefulTrackerViewT[] = [];

		for (const diary of diaries) {
			const womacTracker = diary.trackers_view.find(
				(tracker) => tracker.form_id === UUIDs.forms.womac,
			);
			if (womacTracker)
				womacTrackers.push(womacTracker as StatefulTrackerViewT);
		}

		if (!womacTrackers.length)
			return { improved: 0, total: 0, percentage: 0, calculating: 0 };

		// Calculate improvement for each tracker
		let improvedCount = 0;
		let totalCount = 0;
		let calculatingCount = 0;

		// Process each tracker group
		for (const tracker of womacTrackers) {
			if (!tracker.entries_view || tracker.entries_view.length < 2) {
				calculatingCount++;
			} else {
				totalCount++;

				// Sort by calculated_entry_time to ensure chronological order
				const sortedEntries = [...tracker.entries_view].sort((a, b) => {
					const timeA = a.calculated_entry_time
						? new Date(a.calculated_entry_time).getTime()
						: 0;
					const timeB = b.calculated_entry_time
						? new Date(b.calculated_entry_time).getTime()
						: 0;
					return timeA - timeB;
				});

				const firstEntry = sortedEntries[0];
				const lastEntry = sortedEntries[sortedEntries.length - 1];

				const firstEntryValue = firstEntry?.entry_values.find(
					(ev) => ev.input_id === UUIDs.womacInputs.womacTotal,
				);

				const lastEntryValue = lastEntry?.entry_values.find(
					(ev) => ev.input_id === UUIDs.womacInputs.womacTotal,
				);

				if (!firstEntry || !lastEntry) continue;

				// Calculate total WOMAC score reduction
				const firstTotal = Number(firstEntryValue?.value) || 0;
				const lastTotal = Number(lastEntryValue?.value) || 0;

				// If WOMAC total score reduced by more than 8 points
				if (firstTotal - lastTotal > 8) {
					improvedCount++;
				}
			}
		}

		return {
			improved: improvedCount,
			total: totalCount,
			calculating: calculatingCount,
			percentage:
				totalCount > 0 ? Math.round((improvedCount / totalCount) * 100) : 0,
		};
	}, [diaries]);

	return (
		<div className="flex flex-col gap-3 pt-4 border-t">
			<div className="flex justify-between items-center gap-2">
				<h3 className="font-medium text-sm">WOMAC Score Improvement</h3>
				<div
					className={`flex items-center gap-1 text-xs ${
						improvementStats.percentage === 0
							? "text-slate-600"
							: improvementStats.percentage >= 50
								? "text-green-600"
								: improvementStats.percentage >= 30
									? "text-amber-600"
									: "text-red-600"
					}`}
				>
					<span className="font-medium">
						{improvementStats.percentage ?? "?"}%
					</span>
					<span className="hidden sm:inline">improvement rate</span>
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<div className="text-slate-700 text-xs">
						{improvementStats.improved === 0
							? "No "
							: `${improvementStats.improved} of ${improvementStats.total} `}
						patients with WOMAC score reduced by more than 8 points so far.{" "}
					</div>
				</div>
				<span className="text-slate-500 text-xs">
					(
					{improvementStats.calculating > 0 && (
						<span>
							{improvementStats.calculating} patient
							{improvementStats.calculating !== 1 ? "s" : ""} still calculating
						</span>
					)}
					)
				</span>
			</div>
		</div>
	);
}
